import { useMyBoxContext } from '@fff-web/fff-box';
import { useUserContext } from '@fff-web/fff-utilities';
import { isExpiredOrCanceled, isNotSubscribedStatus } from '../../../pages/Homepage/helpers';
import {
  ACTIVATE_MEMBERSHIP_URL,
  HOMEPAGE_EXPERIENCE,
  INVITE_FRIEND_PAGE_URL,
  REACTIVATE_MY_MEMBERSHIP_PAGE_URL,
  REACTIVATE_TODAY_PAGE_URL,
  UPGRADE_PAGE_URL,
} from '../../../utils/constants';
import {
  handleTrackAndRedirect,
  trackInviteStarted,
  trackReactivationStarted,
  trackUpgradeStarted,
} from '../../../utils/track';
import { CTAS, FFFTRIAL_CTAS } from '../constants';

interface Props {
  accountPlan: string;
}

function Button({ onClick, children }: { onClick: () => void; children: string }) {
  return (
    <button type="button" className="fff-btn-primary" onClick={onClick}>
      {children}
    </button>
  );
}

function CTAButton({ accountPlan }: Props) {
  const { userInfo, membershipData } = useUserContext();
  const { myBox } = useMyBoxContext();
  const isSubscribed = !isNotSubscribedStatus(myBox.data.status, userInfo);

  // TODO: Remove fallback to deprecated userInfo.user.subscriptionState when membership API is fully reliable.
  const membershipState =
    membershipData?.membership_state?.toLowerCase() ??
    userInfo?.user?.subscriptionState?.toLowerCase();
  const isExpiredMember = membershipState === 'expired';
  const isUserExpired = isExpiredMember;
  const isUserExpiredOrCanceled = isExpiredOrCanceled(membershipState);

  const isFFFTrial = userInfo?.user?.planCode === 'ffftrial';
  const role = userInfo?.user?.role;
  const showInviteCTA = (accountPlan === 'ANNUAL' && isSubscribed) || role === 'CLUB';

  const handleFFFTrialActive = () => {
    handleTrackAndRedirect(
      () => trackReactivationStarted(FFFTRIAL_CTAS.active, isUserExpired, membershipState),
      ACTIVATE_MEMBERSHIP_URL,
    );
  };

  const handleReactivateOnClick = () => {
    handleTrackAndRedirect(
      () => trackReactivationStarted(CTAS.reactivate, isUserExpired, membershipState),
      REACTIVATE_MY_MEMBERSHIP_PAGE_URL,
    );
  };

  const handleSendFreeBoxClick = () => {
    handleTrackAndRedirect(
      () => trackInviteStarted(CTAS.invite, HOMEPAGE_EXPERIENCE),
      INVITE_FRIEND_PAGE_URL,
    );
  };

  const handleClickUpgrade = () => {
    handleTrackAndRedirect(() => trackUpgradeStarted(CTAS.upgrade), UPGRADE_PAGE_URL);
  };

  const handleFFFTrialCanceled = () => {
    handleTrackAndRedirect(
      () => trackReactivationStarted(FFFTRIAL_CTAS.canceled, false, 'Trial'),
      REACTIVATE_TODAY_PAGE_URL,
    );
  };

  const handleFFFTrialExpired = () => {
    handleTrackAndRedirect(
      () => trackReactivationStarted(FFFTRIAL_CTAS.expired, true, 'Trial'),
      REACTIVATE_MY_MEMBERSHIP_PAGE_URL,
    );
  };

  const FFFTRIAL_ENUMS = {
    ACTIVE: {
      onClick: handleFFFTrialActive,
      label: FFFTRIAL_CTAS.active,
    },
    CANCELED: {
      onClick: handleFFFTrialCanceled,
      label: FFFTRIAL_CTAS.canceled,
    },
    EXPIRED: {
      onClick: handleFFFTrialExpired,
      label: FFFTRIAL_CTAS.expired,
    },
    active: {
      onClick: handleFFFTrialActive,
      label: FFFTRIAL_CTAS.active,
    },
    canceled: {
      onClick: handleFFFTrialCanceled,
      label: FFFTRIAL_CTAS.canceled,
    },
    expired: {
      onClick: handleFFFTrialExpired,
      label: FFFTRIAL_CTAS.expired,
    },
  };

  if (isFFFTrial) {
    const trial =
      FFFTRIAL_ENUMS[membershipState as keyof typeof FFFTRIAL_ENUMS] ?? FFFTRIAL_ENUMS.ACTIVE;

    return <Button onClick={trial.onClick}>{trial.label}</Button>;
  }

  return (
    <>
      {!isSubscribed && (
        <a className="fff-btn-primary" href={ACTIVATE_MEMBERSHIP_URL}>
          {CTAS.activateMembership}
        </a>
      )}
      {isUserExpiredOrCanceled && isSubscribed ? (
        <Button onClick={handleReactivateOnClick}>{CTAS.reactivate}</Button>
      ) : showInviteCTA ? (
        <Button onClick={handleSendFreeBoxClick}>{CTAS.invite}</Button>
      ) : (
        isSubscribed && <Button onClick={handleClickUpgrade}>{CTAS.upgrade}</Button>
      )}
    </>
  );
}

export default CTAButton;
